/**
 * AutoImportToast — global, persistent progress indicator for an
 * in-flight Skip Review auto-approve job.
 *
 * Mounted once in `Layout.tsx` so it survives every route change.
 * Reads the active job id from localStorage via
 * `useActiveAutoImportJobId` (which subscribes to same-tab and
 * cross-tab change events), polls the status endpoint while a job
 * is running, and renders a fixed-position card in the bottom-right
 * corner of the viewport. When no job is active, it renders null.
 *
 * Why a global toast instead of a banner on /ingestion: an auto-
 * import for a 6000-row spreadsheet runs ~10 minutes server-side.
 * A banner that's only visible while the user happens to be on
 * /ingestion gives the impression the work has stalled the moment
 * they navigate to /properties to spot-check a record. Pinning
 * progress to the Layout makes it ambient — visible everywhere,
 * dismissable, never blocking work.
 */

import { Link } from 'react-router-dom';
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  X,
} from 'lucide-react';
import {
  useAutoImportStatus,
  useActiveAutoImportJobId,
  clearActiveAutoImport,
  type AutoImportProgress,
} from '../hooks/useAutoImportFlow';

export default function AutoImportToast() {
  const jobId = useActiveAutoImportJobId();
  // Status query stays disabled (and doesn't poll) until we actually
  // have a job id, so unmounting the toast doesn't leave a poller
  // running in the React Query cache.
  const { data, isLoading } = useAutoImportStatus(jobId, {
    enabled: Boolean(jobId),
  });

  if (!jobId) return null;

  return (
    <div
      // Fixed bottom-right, z-index above page content but below
      // modal backdrops (modal system uses z-50). Capped width so
      // it never crowds the page; min-w keeps the progress bar
      // legible at small percents.
      className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[calc(100vw-3rem)] shadow-lg rounded-lg overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <ToastBody jobId={jobId} data={data ?? null} isLoading={isLoading} />
    </div>
  );
}

interface ToastBodyProps {
  jobId: string;
  data: AutoImportProgress | null;
  isLoading: boolean;
}

function ToastBody({ jobId, data, isLoading }: ToastBodyProps) {
  // Initial mount before first poll resolves: a thin "Starting…"
  // strip so the user gets immediate confirmation that the kickoff
  // landed (otherwise the toast would silently exist with no copy).
  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200">
        <Loader2 className="w-5 h-5 text-amber-600 animate-spin shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-900">
            Starting auto-import…
          </p>
          <p className="text-xs text-amber-700 mt-0.5 truncate">
            Job {jobId.slice(0, 8)}…
          </p>
        </div>
        <DismissButton onDismiss={clearActiveAutoImport} />
      </div>
    );
  }

  const { status, total, completed, failed, errors } = data;
  const done = completed + failed;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const isFinished = status === 'completed' || status === 'completed_with_errors';

  // Color the card by terminal state. Running = amber (matches the
  // Skip Review checkbox); fully-done success = green; partial
  // failure = red so the user can spot it without reading.
  const palette =
    status === 'completed'
      ? {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-900',
          subtext: 'text-green-700',
          bar: 'bg-green-600',
          barBg: 'bg-green-100',
          icon: <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />,
        }
      : status === 'completed_with_errors'
        ? {
            bg: 'bg-red-50',
            border: 'border-red-200',
            text: 'text-red-900',
            subtext: 'text-red-700',
            bar: 'bg-red-600',
            barBg: 'bg-red-100',
            icon: <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />,
          }
        : {
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            text: 'text-amber-900',
            subtext: 'text-amber-700',
            bar: 'bg-amber-600',
            barBg: 'bg-amber-100',
            icon: <Loader2 className="w-5 h-5 text-amber-600 animate-spin shrink-0" />,
          };

  const titleText = isFinished
    ? failed > 0
      ? `Auto-import done — ${completed}/${total} ok, ${failed} failed`
      : `Auto-import complete — ${completed} record${completed === 1 ? '' : 's'}`
    : `Auto-importing ${done} of ${total} (${percent}%)`;

  const detailText = isFinished
    ? failed > 0
      ? 'Some records need manual review.'
      : 'Imported rows are flagged Unreviewed until verified.'
    : "Running in the background — feel free to keep working.";

  return (
    <div className={`flex items-start gap-3 p-4 ${palette.bg} border ${palette.border}`}>
      {palette.icon}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${palette.text}`}>{titleText}</p>
        <p className={`text-xs ${palette.subtext} mt-0.5`}>{detailText}</p>

        <div
          className={`mt-2 h-1.5 w-full ${palette.barBg} rounded-full overflow-hidden`}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full ${palette.bar} transition-[width] duration-300 ease-out`}
            style={{ width: `${percent}%` }}
          />
        </div>

        {/* Surface the most recent error message inline so the user
            doesn't have to dig into the reviewer to know what kind
            of thing is failing. Truncated; full list lives on the
            reviewer page. */}
        {failed > 0 && errors.length > 0 && (
          <p className={`text-xs ${palette.subtext} mt-2 truncate`}>
            Latest error: <span className="font-mono">{errors[errors.length - 1].message}</span>
          </p>
        )}

        {/* Follow-up actions only after the job settles. While
            running, the user has the global nav for everything else
            and we don't want extra links cluttering the toast. */}
        {isFinished && (
          <div className="flex gap-3 mt-3">
            {failed > 0 ? (
              <Link
                to={`/ingestion/review/${jobId}`}
                onClick={clearActiveAutoImport}
                className="text-xs font-medium underline hover:no-underline"
              >
                Open reviewer
              </Link>
            ) : (
              <Link
                to="/properties"
                onClick={clearActiveAutoImport}
                className="text-xs font-medium underline hover:no-underline"
              >
                View properties
              </Link>
            )}
            <button
              onClick={clearActiveAutoImport}
              className="text-xs font-medium underline hover:no-underline"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
      {!isFinished && <DismissButton onDismiss={clearActiveAutoImport} />}
    </div>
  );
}

function DismissButton({ onDismiss }: { onDismiss: () => void }) {
  return (
    <button
      onClick={onDismiss}
      className="p-1 text-gray-400 hover:text-gray-600 rounded shrink-0"
      title="Hide this toast (the import keeps running)"
      aria-label="Dismiss auto-import toast"
    >
      <X className="w-4 h-4" />
    </button>
  );
}
